import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";

import { withStyles } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";

import CheckBoxIcon from "@material-ui/icons/CheckBox";
import ClearIcon from "@material-ui/icons/Clear";
import SearchIcon from "@material-ui/icons/Search";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import PublishIcon from "@material-ui/icons/Publish";

import { useAlert } from "react-alert";

import { getArtist, postPrivate } from "../../../adminModel/auctions";
import { getFindById, findlot } from "../../../adminModel/lots";


// ============================================================
// STYLES
// ============================================================

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: "#fafafa",
  },

  header: {
    marginBottom: theme.spacing(3),
  },

  title: {
    fontWeight: 700,
    color: "#222",
  },

  subtitle: {
    marginTop: theme.spacing(0.5),
    color: "#777",
  },

  filterCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    border: "1px solid #e5e5e5",
    boxShadow: "none",
    backgroundColor: "#fff",
  },

  searchField: {
    backgroundColor: "#fff",

    "& .MuiOutlinedInput-root": {
      height: 42,
    },
  },

  actionButton: {
    height: 42,
    textTransform: "none",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },

  tableContainer: {
    border: "1px solid #e5e5e5",
    borderRadius: 6,
    backgroundColor: "#fff",
    boxShadow: "none",
  },

  table: {
    minWidth: 850,
  },

  lotNumber: {
    cursor: "pointer",
    color: theme.palette.primary.main,
    fontWeight: 600,

    "&:hover": {
      textDecoration: "underline",
    },
  },

  lotTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  artistName: {
    color: "#444",
  },

  price: {
    fontWeight: 500,
    whiteSpace: "nowrap",
  },

  statusText: {
    fontSize: 13,
    fontWeight: 600,
  },

  emptyState: {
    padding: theme.spacing(6),
    textAlign: "center",
  },

  pagination: {
    borderTop: "1px solid #e5e5e5",
  },

  backdrop: {
    zIndex: theme.zIndex.drawer + 9999,
    color: "#fff",
  },

  dialogHeader: {
    paddingBottom: theme.spacing(1),
  },

  dialogSubtitle: {
    color: "#777",
    fontSize: 13,
  },

  searchArtistContainer: {
    marginBottom: theme.spacing(3),
  },

  artistList: {
    maxHeight: 220,
    overflowY: "auto",
    border: "1px solid #e0e0e0",
    borderRadius: 6,
    backgroundColor: "#fff",
  },

  artistItem: {
    display: "flex",
    alignItems: "center",
    padding: "12px 15px",
    cursor: "pointer",
    borderBottom: "1px solid #eee",
    transition: "background-color 0.15s ease",

    "&:last-child": {
      borderBottom: "none",
    },

    "&:hover": {
      backgroundColor: "#f7f9fc",
    },
  },

  artistSelected: {
    backgroundColor: "#eef4ff !important",
  },

  radio: {
    width: 20,
    height: 20,
    borderRadius: "50%",
    border: "2px solid #bdbdbd",
    marginRight: 14,
    backgroundColor: "#fff",
    boxSizing: "border-box",
    flexShrink: 0,
  },

  radioSelected: {
    border: "6px solid #3f51b5",
  },

  selectedArtist: {
    color: "#3f51b5",
    fontWeight: 600,
  },

  lotPreview: {
    marginBottom: theme.spacing(3),
    backgroundColor: "#fafafa",
    border: "1px solid #e5e5e5",
    boxShadow: "none",
  },

  lotImage: {
    width: "100%",
    height: 90,
    backgroundSize: "contain",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundColor: "#f1f1f1",
    borderRadius: 4,
  },

  dialogActions: {
    padding: theme.spacing(2),
  },
}));


// ============================================================
// TABLE CELL
// ============================================================

const StyledTableCell = withStyles(() => ({
  head: {
    backgroundColor: "#f5f6f8",
    color: "#333",
    fontWeight: 700,
    fontSize: 13,
    padding: "13px 16px",
    borderBottom: "1px solid #ddd",
  },

  body: {
    fontSize: 13,
    padding: "13px 16px",
    color: "#333",
    borderBottom: "1px solid #eee",
    verticalAlign: "middle",
  },
}))(TableCell);


// ============================================================
// COMPONENT
// ============================================================

export default function View() {
  const classes = useStyles();
  const alert = useAlert();

  const { auctionName, auctionId, setId } = useParams();

  // ==========================================================
  // DATA STATES
  // ==========================================================

  const [getData, setGetData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);


  // ==========================================================
  // ASSIGN DIALOG STATES
  // ==========================================================

  const [open, setOpen] = useState(false);

  const [lotId, setLotId] = useState("");
  const [lotDetails, setLotDetails] = useState("");

  const [titleDetails, setTitleDetails] = useState("");
  const [artistDetails, setArtistDetails] = useState("");

  const [imageUrl, setImageUrl] = useState("");

  const [artistID, setArtistID] = useState("");
  const [searchArtist, setSearchArtist] = useState("");
  const [artistData, setArtistData] = useState([]);

  const [dialogMsg, setDialogMsg] = useState({
    type: "",
    text: "",
  });


  // ==========================================================
  // FETCH LOTS
  // ==========================================================

  const fetchAuction = useCallback(
    async (searchValue = "", auctionIdValue = "") => {
      setLoading(true);

      try {
        const results = await findlot(
          searchValue,
          auctionIdValue
        );

        if (!results || results === false) {
          setGetData([]);
          return;
        }

        setGetData(results.data || []);
      } catch (error) {
        console.error("Error loading lots:", error);
        setGetData([]);

        alert.error("Unable to load auction lots.");
      } finally {
        setLoading(false);
      }
    },
    [alert]
  );


  // ==========================================================
  // INITIAL LOAD
  // ==========================================================

  useEffect(() => {
    setPage(0);

    fetchAuction("", auctionId);
  }, [fetchAuction, auctionId]);


  // ==========================================================
  // SEARCH
  // ==========================================================

  const handleSearch = () => {
    setPage(0);

    fetchAuction(search.trim(), auctionId);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearch("");
    setPage(0);

    fetchAuction("", auctionId);
  };


  // ==========================================================
  // IMPORT / UPLOAD
  // ==========================================================

  const handleGotoUpload = () => {
    window.location.href =
      `/app/auctions/import/lots/${auctionId}/${setId}`;
  };


  // ==========================================================
  // LOT DETAILS
  // ==========================================================

  const handleLotDetails = (
    event,
    auctionID,
    lotNumber,
    setID,
    title
  ) => {
    window.location.href =
      `/app/lot/explore/${encodeURIComponent(
        title
      )}/${lotNumber}/${auctionID}/${lotNumber}/${setID}`;
  };


  // ==========================================================
  // OPEN ASSIGN DIALOG
  // ==========================================================

  const handleClickOpen = async (
    lotID,
    lotNumber,
    artist,
    title,
    auctionIdValue
  ) => {
    setLotId(lotID);
    setLotDetails(lotNumber);
    setArtistDetails(artist);
    setTitleDetails(title);

    setArtistData([]);
    setArtistID("");
    setSearchArtist("");

    setDialogMsg({
      type: "",
      text: "",
    });

    setImageUrl("");
    setOpen(true);

    setLoading(true);

    try {
      const results = await getFindById(
        auctionIdValue,
        setId,
        lotNumber
      );

      const featuredImage = results.image?.find(
        (img) => img.isFeatured === 1
      );

      setImageUrl(
        featuredImage
          ? featuredImage.img_url
          : ""
      );
    } catch (error) {
      console.error(
        "Could not fetch lot details:",
        error
      );

      alert.error(
        "Could not fetch lot details."
      );
    } finally {
      setLoading(false);
    }
  };


  // ==========================================================
  // ARTIST SEARCH
  // ==========================================================

  const handleSearchArtist = async () => {
    const keyword = searchArtist.trim();

    if (!keyword) {
      setDialogMsg({
        type: "info",
        text: "Please enter an artist name.",
      });

      return;
    }

    setLoading(true);

    try {
      const results = await getArtist(keyword);

      if (
        results.success === false ||
        !results.data ||
        results.data.length === 0
      ) {
        setArtistData([]);

        setDialogMsg({
          type: "error",
          text: "No artist found with that name.",
        });

        return;
      }

      setArtistData(results.data);

      setDialogMsg({
        type: "success",
        text: `Found ${results.data.length} artist(s).`,
      });
    } catch (error) {
      console.error(
        "Artist search failed:",
        error
      );

      setArtistData([]);

      setDialogMsg({
        type: "error",
        text: "Unable to search artist.",
      });
    } finally {
      setLoading(false);
    }
  };


  // ==========================================================
  // ASSIGN LOT
  // ==========================================================

  const handleClickPutToPrivate = async () => {
    setLoading(true);

    try {
      const results = await postPrivate(
        lotId,
        artistID || null
      );

      if (results.success === false) {
        setDialogMsg({
          type: "error",
          text:
            results.msg ||
            "Assignment failed.",
        });

        return;
      }

      alert.success(
        "Lot assigned successfully."
      );

      handleClose();

      fetchAuction(search, auctionId);
    } catch (error) {
      console.error(
        "Private Assignment Error:",
        error
      );

      setDialogMsg({
        type: "error",
        text:
          "System error during assignment.",
      });
    } finally {
      setLoading(false);
    }
  };


  // ==========================================================
  // CLOSE DIALOG
  // ==========================================================

  const handleClose = () => {
    setOpen(false);

    setArtistData([]);
    setArtistID("");
    setSearchArtist("");

    setImageUrl("");

    setDialogMsg({
      type: "",
      text: "",
    });
  };


  // ==========================================================
  // SORT DATA WITHOUT MUTATING STATE
  // ==========================================================

  const sortedData = [...getData].sort(
    (a, b) =>
      Number(a.lot_number) -
      Number(b.lot_number)
  );


  // ==========================================================
  // PAGINATION
  // ==========================================================

  const paginatedData = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );


  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <Paper
      className={classes.root}
      elevation={0}
    >

      {/* ====================================================
          PAGE TITLE
      ==================================================== */}

      <Box className={classes.header}>
        <Typography
          variant="h5"
          className={classes.title}
        >
          Lot Management
        </Typography>

        <Typography
          variant="body2"
          className={classes.subtitle}
        >
          {decodeURIComponent(
            auctionName || ""
          )}
        </Typography>
      </Box>


      {/* ====================================================
          FILTER / ACTIONS
      ==================================================== */}

      <Card
        className={classes.filterCard}
      >
        <Grid
          container
          spacing={2}
          alignItems="center"
        >

          {/* SEARCH */}

          <Grid
            item
            xs={12}
            md={6}
            lg={5}
          >
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label="Search lots"
              placeholder="Search by lot number or artist"
              value={search}
              onChange={(event) =>
                setSearch(event.target.value)
              }
              onKeyDown={
                handleSearchKeyDown
              }
              className={
                classes.searchField
              }
              InputProps={{
                startAdornment: (
                  <SearchIcon
                    fontSize="small"
                    style={{
                      marginRight: 8,
                      color: "#999",
                    }}
                  />
                ),
              }}
            />
          </Grid>


          {/* SEARCH BUTTON */}

          <Grid
            item
            xs={12}
            sm={6}
            md={2}
          >
            <Button
              fullWidth
              variant="contained"
              color="primary"
              className={
                classes.actionButton
              }
              startIcon={
                <SearchIcon />
              }
              onClick={handleSearch}
              disabled={loading}
            >
              Search
            </Button>
          </Grid>


          {/* CLEAR */}

          <Grid
            item
            xs={12}
            sm={6}
            md={2}
          >
            <Button
              fullWidth
              variant="outlined"
              className={
                classes.actionButton
              }
              onClick={
                handleClearSearch
              }
              disabled={
                loading ||
                !search
              }
            >
              Clear
            </Button>
          </Grid>


          {/* IMPORT */}

          <Grid
            item
            xs={12}
            sm={6}
            md={2}
          >
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              className={
                classes.actionButton
              }
              startIcon={
                <PublishIcon />
              }
              onClick={
                handleGotoUpload
              }
            >
              Import
            </Button>
          </Grid>


          {/* UPLOAD */}

          <Grid
            item
            xs={12}
            sm={6}
            md={2}
          >
            <Button
              fullWidth
              variant="contained"
              color="secondary"
              className={
                classes.actionButton
              }
              startIcon={
                <CloudUploadIcon />
              }
              href={`/app/auctions/upload/lots/${auctionId}/${setId}`}
            >
              Upload
            </Button>
          </Grid>

        </Grid>
      </Card>


      {/* ====================================================
          TABLE
      ==================================================== */}

      <TableContainer
        className={
          classes.tableContainer
        }
      >
        <Table
          stickyHeader
          className={classes.table}
        >

          <TableHead>
            <TableRow>

              <StyledTableCell>
                Lot
              </StyledTableCell>

              <StyledTableCell>
                Artist
              </StyledTableCell>

              <StyledTableCell>
                Starting Price
              </StyledTableCell>

              <StyledTableCell>
                Hammer Price
              </StyledTableCell>

              <StyledTableCell align="center">
                Status
              </StyledTableCell>

              <StyledTableCell align="right">
                Action
              </StyledTableCell>

            </TableRow>
          </TableHead>


          <TableBody>

            {paginatedData.length > 0 ? (

              paginatedData.map(
                (row) => (
                  <TableRow
                    hover
                    key={row.id}
                  >

                    {/* LOT */}

                    <StyledTableCell>

                      <Box
                        className={
                          classes.lotTitle
                        }
                      >

                        <Typography
                          className={
                            classes.lotNumber
                          }
                          onClick={(
                            event
                          ) =>
                            handleLotDetails(
                              event,
                              row.auction_id,
                              row.lot_number,
                              row.set_id,
                              row.title
                            )
                          }
                        >
                          {row.lot_number}
                        </Typography>

                        <Typography
                          variant="body2"
                        >
                          -
                        </Typography>

                        <Typography
                          variant="body2"
                        >
                          {row.title ||
                            "Untitled"}
                        </Typography>

                      </Box>

                    </StyledTableCell>


                    {/* ARTIST */}

                    <StyledTableCell>
                      <Typography
                        className={
                          classes.artistName
                        }
                      >
                        {row.artist ||
                          "No Artist"}
                      </Typography>
                    </StyledTableCell>


                    {/* STARTING PRICE */}

                    <StyledTableCell>
                      <Typography
                        className={
                          classes.price
                        }
                      >
                        {row.starting_price !=
                        null
                          ? Number(
                              row.starting_price
                            ).toLocaleString()
                          : "-"}
                      </Typography>
                    </StyledTableCell>


                    {/* HAMMER PRICE */}

                    <StyledTableCell>
                      <Typography
                        className={
                          classes.price
                        }
                      >
                        {row.hp != null
                          ? Number(
                              row.hp
                            ).toLocaleString()
                          : "-"}
                      </Typography>
                    </StyledTableCell>


                    {/* STATUS */}

                    <StyledTableCell align="center">

                      {row.lot_status ===
                      "SOLD" ? (

                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <CheckBoxIcon
                            style={{
                              color:
                                "#2e7d32",
                            }}
                          />

                          <Typography
                            className={
                              classes.statusText
                            }
                            style={{
                              color:
                                "#2e7d32",
                              marginLeft: 4,
                            }}
                          >
                            Sold
                          </Typography>
                        </Box>

                      ) : [
                          "PASS",
                          "UNSOLD",
                        ].includes(
                          row.lot_status
                        ) ? (

                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <ClearIcon
                            style={{
                              color:
                                "#d32f2f",
                            }}
                          />

                          <Typography
                            className={
                              classes.statusText
                            }
                            style={{
                              color:
                                "#d32f2f",
                              marginLeft: 4,
                            }}
                          >
                            {row.lot_status}
                          </Typography>
                        </Box>

                      ) : (

                        <Typography
                          className={
                            classes.statusText
                          }
                        >
                          {row.lot_status ||
                            "-"}
                        </Typography>

                      )}

                    </StyledTableCell>


                    {/* ACTION */}

                    <StyledTableCell align="right">

                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        className={
                          classes.actionButton
                        }
                        onClick={() =>
                          handleClickOpen(
                            row.id,
                            row.lot_number,
                            row.artist,
                            row.title,
                            row.auction_id
                          )
                        }
                      >
                        Assign
                      </Button>

                    </StyledTableCell>

                  </TableRow>
                )
              )

            ) : (

              <TableRow>

                <TableCell
                  colSpan={6}
                >
                  <Box
                    className={
                      classes.emptyState
                    }
                  >
                    <Typography
                      variant="body1"
                      color="textSecondary"
                    >
                      {loading
                        ? "Loading lots..."
                        : "No lots found."}
                    </Typography>

                    {!loading &&
                      search && (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          style={{
                            marginTop: 6,
                          }}
                        >
                          Try a different
                          search keyword.
                        </Typography>
                      )}
                  </Box>
                </TableCell>

              </TableRow>

            )}

          </TableBody>

        </Table>
      </TableContainer>


      {/* ====================================================
          PAGINATION
      ==================================================== */}

      <TablePagination
        className={
          classes.pagination
        }
        rowsPerPageOptions={[
          20,
          50,
          100,
        ]}
        component="div"
        count={sortedData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(
          event,
          newPage
        ) => {
          setPage(newPage);
        }}
        onRowsPerPageChange={(
          event
        ) => {
          setRowsPerPage(
            parseInt(
              event.target.value,
              10
            )
          );

          setPage(0);
        }}
      />


      {/* ====================================================
          LOADING
      ==================================================== */}

      <Backdrop
        className={
          classes.backdrop
        }
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>


      {/* ====================================================
          ASSIGN DIALOG
      ==================================================== */}

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus
      >

        {/* DIALOG TITLE */}

        <DialogTitle
          className={
            classes.dialogHeader
          }
        >
          <Typography
            variant="h6"
            style={{
              fontWeight: 700,
            }}
          >
            Assign Lot #{lotDetails}
          </Typography>

          <Typography
            className={
              classes.dialogSubtitle
            }
          >
            Search and select the
            master artist record.
          </Typography>
        </DialogTitle>


        {/* ALERT */}

        {dialogMsg.text && (
          <Alert
            severity={
              dialogMsg.type
            }
            style={{
              borderRadius: 0,
            }}
          >
            {dialogMsg.text}
          </Alert>
        )}


        <DialogContent dividers>

          {/* ==================================================
              ARTIST SEARCH
          ================================================== */}

          <Box
            className={
              classes.searchArtistContainer
            }
          >

            <Typography
              variant="caption"
              style={{
                fontWeight: 700,
                color: "#666",
                letterSpacing: 0.3,
              }}
            >
              FIND ARTIST
            </Typography>

            <Box
              display="flex"
              mt={1}
            >

              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Search artist name..."
                value={
                  searchArtist
                }
                onChange={(event) =>
                  setSearchArtist(
                    event.target.value
                  )
                }
                onKeyDown={(
                  event
                ) => {
                  if (
                    event.key ===
                    "Enter"
                  ) {
                    event.preventDefault();
                    handleSearchArtist();
                  }
                }}
              />

              <Button
                variant="contained"
                color="primary"
                className={
                  classes.actionButton
                }
                style={{
                  marginLeft: 8,
                  minWidth: 80,
                }}
                onClick={
                  handleSearchArtist
                }
                disabled={loading}
              >
                Find
              </Button>

            </Box>

          </Box>


          {/* ==================================================
              LOT PREVIEW
          ================================================== */}

          <Card
            variant="outlined"
            className={
              classes.lotPreview
            }
          >

            <CardContent>

              <Grid
                container
                spacing={2}
                alignItems="center"
              >

                <Grid
                  item
                  xs={8}
                >

                  <Typography
                    variant="subtitle1"
                    style={{
                      fontWeight: 700,
                    }}
                  >
                    {titleDetails ||
                      "Untitled"}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="textSecondary"
                  >
                    {artistDetails ||
                      "No Artist"}
                  </Typography>

                </Grid>


                <Grid
                  item
                  xs={4}
                >

                  {imageUrl && (
                    <div
                      className={
                        classes.lotImage
                      }
                      style={{
                        backgroundImage:
                          `url(${imageUrl})`,
                      }}
                    />
                  )}

                </Grid>

              </Grid>

            </CardContent>

          </Card>


          {/* ==================================================
              ARTIST RESULTS
          ================================================== */}

          <Typography
            variant="caption"
            style={{
              fontWeight: 700,
              color: "#666",
              letterSpacing: 0.3,
            }}
          >
            SELECT MASTER ARTIST
          </Typography>


          <Box
            className={
              classes.artistList
            }
            style={{
              marginTop: 8,
            }}
          >

            {artistData.length > 0 ? (

              artistData.map(
                (artist) => {

                  const isSelected =
                    String(
                      artistID
                    ) ===
                    String(
                      artist.rowid
                    );

                  return (
                    <Box
                      key={
                        artist.rowid
                      }
                      className={`${
                        classes.artistItem
                      } ${
                        isSelected
                          ? classes.artistSelected
                          : ""
                      }`}
                      onClick={() =>
                        setArtistID(
                          artist.rowid
                        )
                      }
                    >

                      <div
                        className={`${
                          classes.radio
                        } ${
                          isSelected
                            ? classes.radioSelected
                            : ""
                        }`}
                      />

                      <Typography
                        variant="body2"
                        className={
                          isSelected
                            ? classes.selectedArtist
                            : ""
                        }
                      >
                        {artist.firstname}{" "}
                        {artist.lastname}

                        {artist.nickname &&
                          ` (${artist.nickname})`}
                      </Typography>

                    </Box>
                  );
                }
              )

            ) : (

              <Box
                py={4}
                textAlign="center"
              >
                <Typography
                  variant="body2"
                  color="textSecondary"
                >
                  No artist selected.
                  <br />
                  Search for an artist
                  above.
                </Typography>
              </Box>

            )}

          </Box>

        </DialogContent>


        {/* ==================================================
            DIALOG ACTIONS
        ================================================== */}

        <DialogActions
          className={
            classes.dialogActions
          }
        >

          <Button
            onClick={
              handleClose
            }
            color="default"
          >
            Cancel
          </Button>

          <Button
            onClick={
              handleClickPutToPrivate
            }
            color="primary"
            variant="contained"
          >
            Confirm Assign
          </Button>

        </DialogActions>

      </Dialog>

    </Paper>
  );
}